import { API } from '../api.js';
import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import styles from './SettingsPage.module.css';

const TABS = [
  { id: 'communities', label: 'Communities' },
  { id: 'profile', label: 'Profile' },
  { id: 'affiliates', label: 'Affiliates' },
  { id: 'payouts', label: 'Payouts' },
  { id: 'account', label: 'Account' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'chat', label: 'Chat' },
  { id: 'payment-methods', label: 'Payment methods' },
  { id: 'payment-history', label: 'Payment history' },
  { id: 'subscription',    label: 'Subscription ↗', external: true },
  { id: 'theme', label: 'Theme' }
];

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, token, refreshUser, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const fileInputRef = useRef(null);

  // Dummy State for form
  const [profile, setProfile] = useState({
    firstName: user?.display_name?.split(' ')[0] || 'Decha',
    lastName: user?.display_name?.split(' ')[1] || 'Wattana',
    url: 'qeduvo.com/@dechawattana',
    bio: 'Building AI automated workflows. Passionate about community.',
    location: 'Bangkok, Thailand'
  });

  async function handleAvatarChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('avatar', file);
    try {
      const res = await fetch(`${API}/api/users/me/avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (res.ok) {
        await refreshUser();
        alert('Profile photo updated successfully!');
      } else {
        const err = await res.json().catch(() => ({}));
        alert('Failed to upload photo: ' + (err.error || res.statusText));
      }
    } catch (err) {
      alert('Error uploading photo: ' + err.message);
    }
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className={styles.section}>
            <h2>Profile</h2>
            
            <div className={styles.formGroup}>
              <label>Profile photo</label>
              <div className={styles.photoUpload}>
                <div className={styles.avatarLarge}>
                  {user?.avatar_url ? <img src={`${API}${user.avatar_url}`} alt="Profile" /> : '👨‍💻'}
                </div>
                <div className={styles.uploadActions}>
                  <button type="button" className="btn btn-outline" onClick={(e) => { e.preventDefault(); fileInputRef.current?.click(); }}>CHANGE PHOTO</button>
                  <input 
                    type="file" 
                    ref={fileInputRef} 
                    style={{ display: 'none' }} 
                    accept="image/*" 
                    onChange={handleAvatarChange} 
                  />
                  <p>Recommended size: 300x300px</p>
                </div>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>First name *</label>
                <input type="text" value={profile.firstName} onChange={e => setProfile({...profile, firstName: e.target.value})} />
                <span className={styles.helpText}>Please use your real name. You can only change it once.</span>
              </div>
              <div className={styles.formGroup}>
                <label>Last name *</label>
                <input type="text" value={profile.lastName} onChange={e => setProfile({...profile, lastName: e.target.value})} />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>Qeduvo URL *</label>
              <input type="text" value={profile.url} disabled className={styles.inputDisabled} />
              <span className={styles.helpText}>To change your URL, you need to be a member for 90 days, have 90 contributions, and 30 followers.</span>
            </div>

            <div className={styles.formGroup}>
              <label>Bio</label>
              <textarea 
                maxLength={150} 
                rows={3} 
                value={profile.bio} 
                onChange={e => setProfile({...profile, bio: e.target.value})} 
              />
              <span className={styles.charCount}>{profile.bio.length} / 150</span>
            </div>

            <div className={styles.formGroup}>
              <label>Location</label>
              <input type="text" value={profile.location} onChange={e => setProfile({...profile, location: e.target.value})} />
            </div>

            <div className={styles.accordion}>
              <div className={styles.accordionHeader}>Social links <span>▼</span></div>
            </div>
            
            <div className={styles.accordion}>
              <div className={styles.accordionHeader}>Membership visibility <span>▼</span></div>
            </div>

            <div className={styles.formActions}>
               <button className="btn btn-primary">UPDATE PROFILE</button>
            </div>
          </div>
        );
      case 'account':
        return (
          <div className={styles.section}>
            <h2>Account</h2>
            
            <div className={styles.settingsBlock}>
               <div className={styles.blockInfo}>
                 <h3>Email</h3>
                 <p>{user?.email || 'decha@example.com'}</p>
               </div>
               <button className="btn btn-outline">CHANGE EMAIL</button>
            </div>
            <div className={styles.divider}></div>
            
            <div className={styles.settingsBlock}>
               <div className={styles.blockInfo}>
                 <h3>Password</h3>
                 <p>Last changed 3 months ago</p>
               </div>
               <button className="btn btn-outline">CHANGE PASSWORD</button>
            </div>
            <div className={styles.divider}></div>
            
            <div className={styles.settingsBlock}>
               <div className={styles.blockInfo}>
                 <h3>Timezone</h3>
                 <select className={styles.dropdown}>
                   <option>(GMT+07:00) Asia/Bangkok</option>
                 </select>
               </div>
            </div>
            <div className={styles.divider}></div>

            <div className={styles.settingsBlock}>
               <div className={styles.blockInfo}>
                 <h3>Log out of all devices</h3>
                 <p>Log out of all other active sessions.</p>
               </div>
               <button className="btn btn-outline" onClick={() => { logout(); navigate('/login'); }}>LOG OUT EVERYWHERE</button>
            </div>
          </div>
        );
      case 'theme':
        return (
          <div className={styles.section}>
            <h2>Theme</h2>
            <div className={styles.themeSelector}>
               <select className={styles.dropdown}>
                 <option>Light (default)</option>
                 <option>Dark</option>
                 <option>System</option>
               </select>
            </div>
          </div>
        );
      default:
        return (
          <div className={styles.section}>
            <h2>{TABS.find(t => t.id === activeTab)?.label}</h2>
            <div className="flex-center text-muted" style={{ height: 200 }}>
              This section is under construction.
            </div>
          </div>
        );
    }
  };

  return (
    <div className={styles.settingsContainer}>
      <div className={styles.sidebar}>
        <div className={styles.backBtn} onClick={() => navigate('/')}>
          <span className={styles.backIcon}>←</span> Back to Qeduvo
        </div>
        <nav className={styles.navMenu}>
          {TABS.map(tab => (
            <div
              key={tab.id}
              className={`${styles.navItem} ${activeTab === tab.id ? styles.active : ''}`}
              onClick={() => tab.external ? navigate('/subscription') : setActiveTab(tab.id)}
            >
              {tab.label}
            </div>
          ))}
        </nav>
      </div>
      <div className={styles.mainContent}>
        {renderContent()}
      </div>
    </div>
  );
}